import cx from 'classnames';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { DesignSystemContextProvider } from '@sb/design-system';

import {
  ChatHeader,
  DEFAULT_CHAT_TITLE,
  type ChatHeaderLeftAction,
  type ChatHeaderMenuItem,
} from './ChatHeader';
import { CHAT_PANEL_CARD_WIDTH } from './chatPanelConstants';
import { ChatPanelExpanded } from './ChatPanelExpanded';
import { ChatStage } from './ChatStage';
import type { ChatWarning } from './ChatWarnings';

interface ChatPanelOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  body?: ReactNode;
  footer?: ReactNode;
  title?: string;
  /**
   * Optional kebab-menu items forwarded to the ChatHeader. The header
   * owns the popover; the caller just supplies the actions.
   */
  menuItems?: ChatHeaderMenuItem[];
  /** Optional left-column button (e.g. a back arrow). */
  leftAction?: ChatHeaderLeftAction;
  /** Standing agent-configuration warnings, shown off an icon in the header. */
  warnings?: ChatWarning[];
}

/**
 * Floating chat card with an invisible backdrop that catches clicks
 * to dismiss. The positioning constants below anchor the card to the
 * bottom-right of the viewport; the entrance/exit keyframes (defined
 * in `injectKeyframes`) scale-and-blur it in from the launcher
 * position when opening and reverse on close.
 */
const NAV_BAR_CLEARANCE = 120;
const EDGE_MARGIN = 23;
const TOP_MARGIN = 30;
const ENTRANCE_DURATION = '0.35s';
const ENTRANCE_CURVE = 'cubic-bezier(0.16, 1, 0.3, 1)';
const EXIT_DURATION = '0.2s';
const EXIT_CURVE = 'cubic-bezier(0.36, 0, 0.66, -0.56)';
const EXIT_DURATION_MS = 200;

const KEYFRAME_STYLE_ID = 'routine-agent-chat-overlay-keyframes';

function injectKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAME_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = KEYFRAME_STYLE_ID;

  style.textContent = `
    @keyframes _chatBackdropIn  { from { opacity: 0; } to { opacity: 1; } }
    @keyframes _chatBackdropOut { from { opacity: 1; } to { opacity: 0; } }
    @keyframes _chatCardIn {
      from { opacity: 0; transform: scale(0.78); filter: blur(8px); }
      to   { opacity: 1; transform: scale(1);    filter: blur(0px); }
    }
    @keyframes _chatCardOut {
      from { opacity: 1; transform: scale(1);    filter: blur(0px); }
      to   { opacity: 0; transform: scale(0.85); filter: blur(6px); }
    }
  `;

  document.head.appendChild(style);
}

export function ChatPanelOverlay({
  isOpen,
  onClose,
  body,
  footer,
  title,
  menuItems,
  leftAction,
  warnings,
}: ChatPanelOverlayProps) {
  const [isClosing, setIsClosing] = useState(false);

  const [menuLayerElement, setMenuLayerElement] =
    useState<HTMLDivElement | null>(null);

  const closingTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const prevIsOpenRef = useRef(isOpen);

  useLayoutEffect(() => {
    injectKeyframes();
  }, []);

  // Drive the closing animation off isOpen transitions: when isOpen
  // flips false, we delay the actual unmount by EXIT_DURATION_MS so
  // the exit keyframes can play. useLayoutEffect ensures isClosing is
  // set before paint so a programmatic close still animates instead
  // of disappearing instantly.
  useLayoutEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (isOpen) {
      if (closingTimerRef.current) {
        clearTimeout(closingTimerRef.current);
        closingTimerRef.current = undefined;
      }

      setIsClosing(false);
    } else if (wasOpen) {
      setIsClosing(true);

      closingTimerRef.current = setTimeout(() => {
        setIsClosing(false);
        closingTimerRef.current = undefined;
      }, EXIT_DURATION_MS);
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      if (closingTimerRef.current) clearTimeout(closingTimerRef.current);
    },
    [],
  );

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (typeof document === 'undefined') return null;
  if (!isOpen && !isClosing) return null;

  const overlay = (
    <div
      data-testid="routine-agent-chat-overlay"
      className="tw-fixed tw-inset-0"
      style={{
        // z-index 99999 + isolation: isolate aren't in the design-
        // system token scale; the overlay needs to stack above
        // everything else in the app.
        zIndex: 99999,
        isolation: 'isolate',
      }}
    >
      {/* Backdrop — invisible click-catcher (no background color),
          so the underlying page stays visible. Clicking anywhere
          outside the chat card dismisses the panel. */}
      <div
        onClick={handleBackdropClick}
        role="presentation"
        className="tw-absolute tw-inset-0"
        style={{
          // Animation references the keyframes injected lazily on
          // mount (see `injectKeyframes`).
          animation: isClosing
            ? `_chatBackdropOut ${EXIT_DURATION} ${EXIT_CURVE} forwards`
            : `_chatBackdropIn ${ENTRANCE_DURATION} ${ENTRANCE_CURVE} forwards`,
        }}
      />

      {/* Positioned container — anchors the chat card to the right side
          of the screen, with the card's own bottom-right origin so it
          scales in from the launcher position. */}
      <DesignSystemContextProvider portalContainer={menuLayerElement}>
        <div
          className={cx(
            'tw-absolute',
            'tw-flex',
            'tw-flex-row',
            'tw-justify-end',
            'tw-pointer-events-auto',
          )}
          style={{
            // Position offsets come from the layout constants above;
            // transform-origin + animation drive the entrance/exit
            // choreography off the kebab's bottom-right corner.
            bottom: NAV_BAR_CLEARANCE,
            right: EDGE_MARGIN,
            left: `calc(100% - ${CHAT_PANEL_CARD_WIDTH + EDGE_MARGIN}px)`,
            top: TOP_MARGIN,
            transformOrigin: 'bottom right',
            animation: isClosing
              ? `_chatCardOut ${EXIT_DURATION} ${EXIT_CURVE} forwards`
              : `_chatCardIn ${ENTRANCE_DURATION} ${ENTRANCE_CURVE} forwards`,
          }}
        >
          <ChatPanelExpanded ariaLabel={title ?? DEFAULT_CHAT_TITLE}>
            <ChatStage
              header={
                <ChatHeader
                  title={title}
                  menuItems={menuItems}
                  leftAction={leftAction}
                  warnings={warnings}
                />
              }
              body={body}
              footer={footer}
            />
          </ChatPanelExpanded>
        </div>
      </DesignSystemContextProvider>

      {/* Portal target for the panel's popovers. */}
      <div
        ref={setMenuLayerElement}
        data-testid="routine-agent-chat-menu-layer"
        className="tw-contents"
      />
    </div>
  );

  return createPortal(overlay, document.body);
}
